/**
 * Rate Limit Warning Component
 * Displays rate limiting information and warnings to users
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import './RateLimitWarning.css';

const RateLimitWarning = ({ 
  isLimited, 
  remainingTime, 
  attemptCount, 
  maxAttempts, 
  isApproachingLimit,
  getFormattedTimeRemaining,
  getProgressPercentage,
  type = 'login'
}) => {
  const { t } = useTranslation();

  if (!isLimited && !isApproachingLimit) {
    return null;
  }

  const getWarningMessage = () => {
    if (isLimited) {
      const timeRemaining = getFormattedTimeRemaining();
      return `Too many ${type} attempts. Please try again in ${timeRemaining}.`;
    }
    
    if (isApproachingLimit) {
      const remaining = maxAttempts - attemptCount;
      return `Warning: ${remaining} ${type} attempts remaining.`;
    }
    
    return null;
  };

  const getWarningLevel = () => {
    if (isLimited) return 'error';
    if (isApproachingLimit) return 'warning';
    return 'info';
  };

  const warningLevel = getWarningLevel();
  const message = getWarningMessage();
  const progressPercentage = getProgressPercentage();

  return (
    <div className={`rate-limit-warning rate-limit-warning--${warningLevel}`}>
      <div className="rate-limit-warning__content">
        <div className="rate-limit-warning__icon">
          {warningLevel === 'error' && '🚫'}
          {warningLevel === 'warning' && '⚠️'}
          {warningLevel === 'info' && 'ℹ️'}
        </div>
        
        <div className="rate-limit-warning__message">
          <p className="rate-limit-warning__text">{message}</p>
          
          {!isLimited && (
            <div className="rate-limit-warning__progress">
              <div className="rate-limit-warning__progress-bar">
                <div 
                  className="rate-limit-warning__progress-fill"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <span className="rate-limit-warning__progress-text">
                {attemptCount}/{maxAttempts} attempts
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateLimitWarning;
